// Searchable places for LOT FOR SALE (provinces, cities, villages, developments) built from the live
// inventory, plus the nickname table the handoff asks for (README §4: "aliases (AAV -> Ayala Alabang)").
// Plain data and pure functions only, so the same list powers the header search and the hero search.

import type { Summary } from "./inventory"

export interface PlaceHit {
  label: string
  /** Province, City, or the community's verified label (Village or subdivision, Development, Project…) */
  kind: string
  sub: string
  count: number
  href: string
  icon: string
  alias?: string
}

// nickname -> name as it appears in listings. Extended by LFS-P03-004 (location structure).
export const ALIASES: Record<string, string> = {
  aav: "Ayala Alabang",
  "ayala alabang village": "Ayala Alabang",
  qc: "Quezon City",
  bgc: "Taguig",
  "bonifacio global city": "Taguig",
  "sta rosa": "Santa Rosa",
  "sta. rosa": "Santa Rosa",
  southwoods: "Manila Southwoods",
  "las pinas": "Las Piñas",
  paranaque: "Parañaque",
}

const fold = (s: string) => s.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()

/** Returns the query with a known nickname expanded, e.g. "aav" -> "ayala alabang". */
export function expandAlias(q: string): string {
  const k = fold(q)
  return ALIASES[k] ? fold(ALIASES[k]) : k
}

export function buildPlaces(s: Summary): PlaceHit[] {
  return [
    ...s.provinces.map((p) => ({ label: p.name, kind: "Province" as const, sub: p.region, count: p.count, href: p.href, icon: "map" })),
    ...s.cities.map((c) => ({ label: c.name, kind: "City" as const, sub: c.province, count: c.count, href: c.href, icon: "location_city" })),
    ...s.communities.map((c) => ({
      label: c.name,
      kind: c.label,
      sub: [c.city, c.province].filter(Boolean).join(", "),
      count: c.count,
      href: c.href,
      icon: c.kind === "village" ? "shield_person" : c.type === "industrial" ? "factory" : "holiday_village",
    })),
  ]
}

/** Best matches first: exact start of name, then start of any word, then anywhere. */
export function matchPlaces(places: PlaceHit[], q: string, limit = 8): PlaceHit[] {
  const raw = fold(q)
  if (raw.length < 2) return []
  const s = expandAlias(raw)
  const aliasHit = s !== raw
  const score = (p: PlaceHit) => {
    const n = fold(p.label)
    if (n.startsWith(s)) return 0
    if (n.split(/[\s-]+/).some((w) => w.startsWith(s))) return 1
    if (n.includes(s)) return 2
    return 9
  }
  return places
    .map((p) => ({ p, sc: score(p) }))
    .filter((x) => x.sc < 9)
    .sort((a, b) => a.sc - b.sc || b.p.count - a.p.count)
    .slice(0, limit)
    .map(({ p }) => (aliasHit ? { ...p, alias: q.trim() } : p))
}
